import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { posApi, operationApi } from '../../services/api';
import { Bar } from 'react-chartjs-2';
import InvoiceModal from '../../components/admin/InvoiceModal';
import { QrCode, UserCheck, Tag, Eye, X, Printer, MessageCircle, Timer } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STATUSES = ['pending', 'assigned', 'waiting', 'confirmed', 'on_progress', 'done', 'invoice_release', 'paid', 'invoice_outstanding', 'cancelled'];
const PAYMENT_STATUSES = ['unpaid', 'outstanding', 'paid', 'refunded'];

const statusLabels = {
  pending: 'Pending', assigned: 'Diassign', waiting: 'Waiting', confirmed: 'Confirmed',
  on_progress: 'On Progress', done: 'Done', invoice_release: 'Invoice Release',
  paid: 'Paid', invoice_outstanding: 'Invoice Outstanding', cancelled: 'Cancelled',
};
const paymentLabels = { unpaid: 'Belum Bayar', outstanding: 'Outstanding', paid: 'Lunas', refunded: 'Refund' };

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700', assigned: 'bg-indigo-100 text-indigo-700',
  waiting: 'bg-orange-100 text-orange-700', confirmed: 'bg-blue-100 text-blue-700',
  on_progress: 'bg-purple-100 text-purple-700', done: 'bg-green-100 text-green-700',
  invoice_release: 'bg-teal-100 text-teal-700', paid: 'bg-emerald-100 text-emerald-700',
  invoice_outstanding: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-700',
};
const paymentColors = {
  unpaid: 'bg-red-100 text-red-700', outstanding: 'bg-orange-100 text-orange-700',
  paid: 'bg-green-100 text-green-700', refunded: 'bg-gray-100 text-gray-700',
};

export default function DashboardPage() {
  const [stats, setStats] = useState({ orders: 0, revenue: 0, customers: 0, avgOrder: 0 });
  const [monthlySales, setMonthlySales] = useState({ labels: [], data: [] });
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOrder, setDetailOrder] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

        const [ordersRes] = await Promise.all([
          posApi.orders.list({ per_page: 100, date_from: firstDay, date_to: lastDay }),
        ]);

        const orders = ordersRes.data.data?.data || ordersRes.data.data || [];
        const totalRevenue = orders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
        const uniquePhones = new Set(orders.map(o => o.customer_phone).filter(Boolean));

        setStats({ orders: orders.length, revenue: totalRevenue, customers: uniquePhones.size, avgOrder: orders.length > 0 ? Math.round(totalRevenue / orders.length) : 0 });
        setAllOrders(orders);
        setRecentOrders(orders.slice(0, 5));

        const months = [];
        const salesData = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(d.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' }));
          salesData.push(0);
        }
        try {
          const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().split('T')[0];
          const allRes = await posApi.orders.list({ per_page: 500, date_from: sixMonthsAgo, date_to: lastDay });
          const allOrdersData = allRes.data.data?.data || allRes.data.data || [];
          allOrdersData.forEach(o => {
            const oDate = new Date(o.created_at);
            const monthIdx = (oDate.getFullYear() - now.getFullYear()) * 12 + (oDate.getMonth() - now.getMonth()) + 5;
            if (monthIdx >= 0 && monthIdx < 6) salesData[monthIdx] += parseFloat(o.total) || 0;
          });
        } catch {}
        setMonthlySales({ labels: months, data: salesData });
      } catch (err) { console.error('Dashboard fetch error:', err); } finally { setLoading(false); }
    };
    fetchDashboard();
  }, []);

  useEffect(() => {
    const loadWorkers = async () => {
      try { const res = await operationApi.profiles.list({ per_page: 200 }); setWorkers(res.data.data?.data || res.data.data || []); } catch {}
    };
    loadWorkers();
  }, []);

  const formatPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(p);
  const fmtDate = (d) => new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleStatusUpdate = async (orderId, field, value) => {
    setSaving(true);
    try {
      await posApi.orders.update(orderId, { [field]: value });
      const updated = { ...detailOrder, [field]: value };
      setDetailOrder(updated);
      setRecentOrders(prev => prev.map(o => o.id === orderId ? { ...o, [field]: value } : o));
    } catch (err) { alert('Gagal update status'); }
    finally { setSaving(false); }
  };

  const handleAssignWorker = async (orderId, workerId) => {
    setSaving(true);
    try {
      await posApi.orders.update(orderId, { assigned_worker_id: workerId, status: 'assigned' });
      const worker = workers.find(w => w.id === workerId);
      setDetailOrder(prev => ({ ...prev, assigned_worker_id: workerId, status: 'assigned', assigned_worker: worker }));
      setRecentOrders(prev => prev.map(o => o.id === orderId ? { ...o, assigned_worker_id: workerId, status: 'assigned', assigned_worker: worker } : o));
    } catch (err) { alert('Gagal assign worker'); }
    finally { setSaving(false); }
  };

  const chartData = { labels: monthlySales.labels, datasets: [{ label: 'Penjualan', data: monthlySales.data, backgroundColor: 'rgba(0, 179, 166, 0.5)', borderColor: '#00B3A6', borderWidth: 1, borderRadius: 8 }] };
  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => formatPrice(ctx.raw) } } }, scales: { y: { beginAtZero: true, ticks: { callback: (v) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v } } } };

  // Live timer for on_progress orders
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  function formatElapsed(startedAt) {
    if (!startedAt) return '-';
    const start = new Date(startedAt).getTime();
    const elapsed = Math.floor((now - start) / 1000);
    if (elapsed < 0) return '0:00';
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  if (loading) {
    return <AdminLayout active="Dashboard"><div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#00B3A6', borderTopColor: 'transparent' }} /></div></AdminLayout>;
  }

  return (
    <AdminLayout active="Dashboard">
      <h1 className="text-lg font-semibold text-gray-800 mb-1">Dashboard</h1>
      <p className="text-sm text-gray-500 mb-6">Ringkasan penjualan bulan {new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}</p>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[ 
          { label: 'Total Pesanan', value: stats.orders, icon: '📋', color: 'bg-blue-50 text-blue-600' },
          { label: 'Pendapatan', value: formatPrice(stats.revenue), icon: '💰', color: 'bg-green-50 text-green-600' },
          { label: 'Pelanggan Unik', value: stats.customers, icon: '👥', color: 'bg-purple-50 text-purple-600' },
          { label: 'Rata-rata Pesanan', value: formatPrice(stats.avgOrder), icon: '📊', color: 'bg-orange-50 text-orange-600' },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3"><span className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${card.color}`}>{card.icon}</span></div>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="text-sm text-gray-500 mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Active On-Progress Orders - Live Timer Cards */}
      {(() => {
        const activeOrders = allOrders.filter(o => o.status === 'on_progress');
        if (activeOrders.length === 0) return null;
        return (
          <div className="mb-6">
            <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Timer size={18} className="text-purple-600 animate-pulse" /> Sedang Dikerjakan ({activeOrders.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {activeOrders.map(o => {
                const elapsed = formatElapsed(o.started_at);
                const worker = o.assigned_worker || workers.find(w => w.id === o.assigned_worker_id);
                return (
                  <button
                    key={o.id}
                    onClick={() => setDetailOrder(o)}
                    className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200 shadow-sm hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-purple-600">{o.order_number}</span>
                      <span className="text-[9px] bg-purple-200 text-purple-700 rounded-full px-2 py-0.5 font-medium">ON PROGRESS</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 truncate">{o.customer_name}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] text-purple-500 uppercase font-semibold">Timer</p>
                        <p className="text-lg font-bold text-purple-700 font-mono">{elapsed}</p>
                      </div>
                      {worker && (
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400">Worker</p>
                          <p className="text-xs font-medium text-indigo-600">{worker.full_name}</p>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Chart + Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-bold text-gray-800 mb-4">📈 Penjualan 6 Bulan Terakhir</h2>
          <div className="h-64">{monthlySales.data.some(v => v > 0) ? <Bar data={chartData} options={chartOptions} /> : <div className="flex items-center justify-center h-full text-gray-400 text-sm">Belum ada data penjualan</div>}</div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-bold text-gray-800 mb-4">🕐 Pesanan Terbaru</h2>
          {recentOrders.length === 0 ? <p className="text-sm text-gray-400 text-center py-8">Belum ada pesanan bulan ini</p> : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentOrders.map(o => (
                <button key={o.id} onClick={() => setDetailOrder(o)} className="w-full text-left flex items-center justify-between p-3 bg-gray-50 hover:bg-brand/5 rounded-lg transition-colors border border-transparent hover:border-brand/20">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">{o.customer_name || 'Tanpa Nama'} <span className="text-[10px] text-gray-400 font-mono">{o.order_number}</span></p>
                    <p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="text-right ml-2">
                    <span className="text-sm font-bold text-navy">{formatPrice(o.total)}</span>
                    <span className={`block text-[10px] rounded-full px-1.5 py-0.5 mt-0.5 font-medium ${statusColors[o.status] || 'bg-gray-100 text-gray-700'}`}>{statusLabels[o.status] || o.status}</span>
                  </div>
                  <Eye size={14} className="ml-1 text-gray-300" />
                </button>
              ))}
            </div>
          )}
          <Link to="/admin/orders" className="block mt-3 text-center text-sm text-brand hover:text-brand-dark font-medium">Lihat semua pesanan →</Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[{ to: '/admin/pos', label: '💳 POS', desc: 'Buat pesanan baru' },{ to: '/admin/orders', label: '📋 Pesanan', desc: 'Lihat semua pesanan' },{ to: '/admin/categories', label: '📂 Kategori', desc: 'Kelola kategori layanan' },{ to: '/admin/items', label: '📦 Item', desc: 'Kelola item & paket' }].map(item => (
          <Link key={item.label} to={item.to} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"><h3 className="font-semibold text-gray-900 mb-1">{item.label}</h3><p className="text-sm text-gray-500">{item.desc}</p></Link>
        ))}
      </div>

      {/* Detail Popup with Status + Assign */}
      {detailOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto mt-6">
            <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
              <div><h2 className="text-lg font-bold text-gray-800">{detailOrder.order_number}</h2><p className="text-xs text-gray-400">{fmtDate(detailOrder.created_at)}</p></div>
              <button onClick={() => setDetailOrder(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-gray-400">Pelanggan</p><p className="font-semibold">{detailOrder.customer_name}</p></div>
                <div><p className="text-xs text-gray-400">Telepon</p>
                  <a
                    href={(() => {
                      const phone = (detailOrder.customer_phone || '').replace(/[^0-9]/g, '');
                      const items = detailOrder.items || [];
                      const itemLines = items.map((item, idx) => `${idx + 1}. ${item.name} ×${item.quantity || 1} = ${formatPrice(item.unit_price * (item.quantity || 1))}${item.duration ? ' — ' + item.duration + ' menit' : ''}`).join('\n');
                      const totalDuration = items.reduce((sum, item) => sum + (parseInt(item.duration) || 0) * (item.quantity || 1), 0);
                      const text = encodeURIComponent(
                        `Halo ${detailOrder.customer_name},\n\n` +
                        `Terima kasih telah memesan layanan Klinnovac.\n\n` +
                        `Berikut detail pesanan Anda:\n` +
                        `Nomor Order: ${detailOrder.order_number}\n\n` +
                        `📋 Layanan:\n${itemLines}\n` +
                        `\n💰 Total: ${formatPrice(detailOrder.total)}` +
                        (totalDuration > 0 ? `\n⏱ Est. total pengerjaan: ${totalDuration} menit\n` : '\n') +
                        `\nTim kami akan segera menghubungi Anda.`
                      );
                      return `https://wa.me/${phone}?text=${text}`;
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:text-brand-dark flex items-center gap-1 font-medium"
                  >
                    <MessageCircle size={12} /> {detailOrder.customer_phone}
                  </a>
                </div>
                {detailOrder.customer_email && <div><p className="text-xs text-gray-400">Email</p><p className="text-xs">{detailOrder.customer_email}</p></div>}
                {detailOrder.customer_address && <div><p className="text-xs text-gray-400">Alamat</p><p className="text-xs">{detailOrder.customer_address}</p></div>}
                {detailOrder.notes && <div className="col-span-2"><p className="text-xs text-gray-400">Catatan</p><p className="text-xs">{detailOrder.notes}</p></div>}
              </div>

              {/* Status & Payment Control */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Status Pesanan</p>
                  <select
                    value={detailOrder.status}
                    onChange={e => handleStatusUpdate(detailOrder.id, 'status', e.target.value)}
                    className={`w-full px-2 py-1.5 text-xs rounded-lg border-0 font-medium ${statusColors[detailOrder.status] || 'bg-gray-100'}`}
                    disabled={saving}
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Pembayaran</p>
                  <select
                    value={detailOrder.payment_status}
                    onChange={e => handleStatusUpdate(detailOrder.id, 'payment_status', e.target.value)}
                    className={`w-full px-2 py-1.5 text-xs rounded-lg border-0 font-medium ${paymentColors[detailOrder.payment_status] || 'bg-gray-100'}`}
                    disabled={saving}
                  >
                    {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{paymentLabels[s] || s}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Assign Pekerja</p>
                  <select
                    value={detailOrder.assigned_worker_id || ''}
                    onChange={e => { if (e.target.value) handleAssignWorker(detailOrder.id, parseInt(e.target.value)); }}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                    disabled={saving}
                  >
                    <option value="">- Pilih -</option>
                    {workers.filter(w => w.status === 'active').map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                  </select>
                  {detailOrder.assigned_worker && (
                    <p className="text-[10px] text-indigo-600 mt-1 flex items-center gap-1"><UserCheck size={10} /> {detailOrder.assigned_worker.full_name}</p>
                  )}
                  {!detailOrder.assigned_worker && detailOrder.assigned_worker_id && (
                    <p className="text-[10px] text-indigo-600 mt-1">ID: {detailOrder.assigned_worker_id}</p>
                  )}
                </div>
              </div>

              {/* Referral */}
              {detailOrder.referral_code && (
                <div className="bg-brand/5 border border-brand/20 rounded-lg p-3 flex items-center gap-2">
                  <QrCode size={14} className="text-brand" />
                  <p className="text-xs text-gray-700">Referral: <span className="font-mono font-bold">{detailOrder.referral_code}</span></p>
                </div>
              )}

              {/* Items */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Tag size={12} /> Item</h3>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                  {(detailOrder.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs"><span>{item.name} ×{item.quantity}</span><span className="font-medium">{formatPrice(item.unit_price * (item.quantity || 1))}</span></div>
                  ))}
                  <div className="border-t pt-2 mt-1 space-y-0.5">
                    <div className="flex justify-between text-[10px] text-gray-400"><span>Subtotal</span><span>{formatPrice(detailOrder.subtotal)}</span></div>
                    {detailOrder.tax_amount > 0 && <div className="flex justify-between text-[10px] text-gray-400"><span>Pajak</span><span>{formatPrice(detailOrder.tax_amount)}</span></div>}
                    {detailOrder.discount_amount > 0 && <div className="flex justify-between text-[10px] text-gray-400"><span>Diskon</span><span>-{formatPrice(detailOrder.discount_amount)}</span></div>}
                    <div className="flex justify-between text-sm font-bold pt-1 border-t"><span>Total</span><span className="text-brand">{formatPrice(detailOrder.total)}</span></div>
                  </div>
                </div>
              </div>

              {/* Invoice button if invoice_release or paid */}
              {(detailOrder.status === 'invoice_release' || detailOrder.status === 'paid') && (
                <button onClick={() => setInvoiceOrder(detailOrder)} className="w-full py-2 bg-navy text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-navy/80">
                  <Printer size={14} /> Cetak Invoice
                </button>
              )}

              {/* Status Lifecycle */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 mb-2">Workflow Status</h3>
                <div className="flex flex-wrap gap-1">
                  {STATUSES.map(s => (
                    <span key={s} className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${detailOrder.status === s ? (s === 'cancelled' || s === 'invoice_outstanding' ? 'bg-red-600 text-white' : 'bg-brand text-white') : 'bg-gray-100 text-gray-400'}`}>
                      {statusLabels[s] || s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {invoiceOrder && <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} formatPrice={formatPrice} fmtDate={fmtDate} />}
    </AdminLayout>
  );
}